import { CheckCircle2, Layers, Ruler, Package, Truck, ClipboardCheck, ScanLine } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Eyebrow, FadeUp } from "@/components/Reveal";
import { useQuote } from "@/components/QuoteProvider";
import { IMAGES, PROCESS_STEPS } from "@/data/site";

const CAPABILITIES = [
    { icon: Layers, title: "Layer Capabilities", desc: "Single-sided, double-sided, multilayer and metal core PCBs manufactured to customer designs." },
    { icon: Ruler, title: "Engineering Support", desc: "Gerber data review and manufacturability checks before production begins." },
    { icon: ClipboardCheck, title: "Process Control", desc: "Disciplined control of imaging, etching, lamination, drilling and plating stages." },
    { icon: ScanLine, title: "Inspection & Testing", desc: "Visual inspection at every stage and electrical testing of completed boards." },
    { icon: Package, title: "Finishing & Packaging", desc: "Surface finishing, solder masking, legend printing and protective packing." },
    { icon: Truck, title: "Dispatch", desc: "Careful dispatch planning to meet committed delivery timelines." },
];

const PROCESS_IMAGES = [
    { src: IMAGES.prodDrilling, caption: "Drilling / Routing" },
    { src: IMAGES.prodPlating, caption: "Copper Plating" },
    { src: IMAGES.prodMask, caption: "Solder Mask / Legend" },
    { src: IMAGES.prodPanels, caption: "Final PCB Panels" },
];

export default function Infrastructure() {
    const { openQuote } = useQuote();
    return (
        <>
            <PageHero
                crumb="Infrastructure"
                eyebrow="Capabilities · Process · Testing · Packaging"
                titleLines={["Manufacturing", "Infrastructure."]}
                lead="The capabilities, production process and inspection discipline behind every PCB we manufacture."
            />

            {/* Capabilities */}
            <section id="capabilities" className="scroll-mt-24 bg-white py-20 md:py-28" data-testid="infra-capabilities">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <FadeUp>
                        <Eyebrow>Manufacturing Capabilities</Eyebrow>
                        <h2 className="mt-5 max-w-2xl text-3xl font-bold tracking-tight text-navy md:text-4xl">
                            Built for consistent, repeatable PCB production
                        </h2>
                    </FadeUp>
                    <div className="mt-12 grid gap-px border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:grid-cols-3" data-testid="infra-capability-grid">
                        {CAPABILITIES.map((c, i) => (
                            <FadeUp key={c.title} delay={(i % 3) * 0.07} className="bg-white p-7 transition-colors duration-200 hover:bg-slate-50">
                                <c.icon className="h-6 w-6 text-brand-red" strokeWidth={1.5} />
                                <h3 className="mt-5 text-[16px] font-semibold text-navy">{c.title}</h3>
                                <p className="mt-2.5 text-[13.5px] leading-relaxed text-slate-500">{c.desc}</p>
                            </FadeUp>
                        ))}
                    </div>
                </div>
            </section>

            {/* Production process */}
            <section id="production-process" className="scroll-mt-24 border-y border-slate-200 bg-slate-50 py-20 md:py-28" data-testid="infra-process">
                <div className="mx-auto grid max-w-7xl gap-14 px-4 sm:px-6 lg:grid-cols-2 lg:gap-20 lg:px-8">
                    <div>
                        <FadeUp>
                            <Eyebrow>Production Process</Eyebrow>
                            <h2 className="mt-5 text-3xl font-bold tracking-tight text-navy md:text-4xl">
                                From Gerber review to dispatch
                            </h2>
                            <p className="mt-6 text-[15px] leading-relaxed text-slate-600">
                                Every order follows a defined production sequence. Each stage has its
                                own checks, so problems are caught where they occur — not after the
                                boards reach you.
                            </p>
                        </FadeUp>
                        <div className="mt-10 space-y-0 border-l border-slate-300" data-testid="infra-process-steps">
                            {PROCESS_STEPS.map((s, i) => (
                                <FadeUp key={s.num} delay={i * 0.05} className="relative pb-8 pl-10 last:pb-0">
                                    <span className="absolute -left-[7px] top-1 h-3.5 w-3.5 border-2 border-brand-red bg-white" aria-hidden="true" />
                                    <span className="font-mono text-[11px] tracking-[0.2em] text-brand-red">{s.num}</span>
                                    <h3 className="mt-1 text-[16px] font-semibold text-navy">{s.title}</h3>
                                    <p className="mt-1.5 text-[13.5px] leading-relaxed text-slate-500">{s.desc}</p>
                                </FadeUp>
                            ))}
                        </div>
                    </div>
                    <div>
                        <div className="grid grid-cols-2 gap-4" data-testid="infra-process-photos">
                            {PROCESS_IMAGES.map((p, i) => (
                                <FadeUp key={p.caption} delay={i * 0.07} className="relative">
                                    <img
                                        src={p.src}
                                        alt={`${p.caption} — PCB production at Unimicron Technology India`}
                                        className="h-44 w-full object-cover md:h-52"
                                    />
                                    <p className="absolute bottom-0 left-0 bg-navy-dark/90 px-3 py-1.5 font-mono text-[9.5px] uppercase tracking-[0.16em] text-white">
                                        {p.caption}
                                    </p>
                                </FadeUp>
                            ))}
                        </div>
                        <FadeUp className="mt-6 border border-slate-200 bg-white p-5" delay={0.15}>
                            <p className="text-[13px] leading-relaxed text-slate-600">
                                <span className="font-semibold text-navy">Every stage is inspected.</span>{" "}
                                Boards move through drilling, plating, solder masking and finishing with
                                checks at each step, and are electrically tested before approval.
                            </p>
                        </FadeUp>
                    </div>
                </div>
            </section>

            {/* Inspection & testing */}
            <section id="inspection-testing" className="scroll-mt-24 bg-white py-20 md:py-28" data-testid="infra-inspection">
                <div className="mx-auto grid max-w-7xl gap-14 px-4 sm:px-6 lg:grid-cols-2 lg:gap-20 lg:px-8">
                    <FadeUp className="relative order-2 lg:order-1">
                        <img
                            src={IMAGES.factoryQuality}
                            alt="Quality inspection and testing machines at Unimicron Technology India"
                            className="w-full border border-slate-200"
                        />
                        <div className="absolute -bottom-4 -left-4 hidden h-24 w-24 border-b-2 border-l-2 border-brand-red md:block" aria-hidden="true" />
                    </FadeUp>
                    <div className="order-1 lg:order-2">
                        <FadeUp>
                            <Eyebrow>Quality Inspection & Testing</Eyebrow>
                            <h2 className="mt-5 text-3xl font-bold tracking-tight text-navy md:text-4xl">
                                Checked where it matters, at every stage
                            </h2>
                            <p className="mt-6 text-[15px] leading-relaxed text-slate-600">
                                Boards are inspected as they move through production and tested once
                                complete. Only boards that pass our checks are approved for packing.
                            </p>
                        </FadeUp>
                        <div className="mt-8 space-y-px bg-slate-200" data-testid="infra-inspection-points">
                            {[
                                "High-resolution digital inspection of copper patterns and solder masking",
                                "Dedicated bare board HV testing of every batch",
                                "3D solder paste inspection and electrical testing of completed boards",
                                "Final inspection against the customer's specification",
                            ].map((point, i) => (
                                <FadeUp key={point} delay={i * 0.06} className="flex items-start gap-3 bg-white p-4">
                                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-brand-red" strokeWidth={1.75} />
                                    <p className="text-[13.5px] leading-relaxed text-slate-700">{point}</p>
                                </FadeUp>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Packaging */}
            <section id="packaging" className="scroll-mt-24 border-t border-slate-200 bg-slate-50 py-20 md:py-28" data-testid="infra-packaging">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="grid gap-12 lg:grid-cols-2 lg:gap-20">
                        <FadeUp>
                            <Eyebrow>Packaging & Dispatch</Eyebrow>
                            <h2 className="mt-5 text-3xl font-bold tracking-tight text-navy md:text-4xl">
                                Boards that arrive the way they left
                            </h2>
                            <p className="mt-6 text-[15px] leading-relaxed text-slate-600">
                                Finished PCBs are cleaned, individually protected, batch-packed and
                                labelled for traceability. Packaging is chosen to protect boards
                                through transit, so they reach your facility ready for assembly.
                            </p>
                            <button
                                onClick={openQuote}
                                data-testid="infra-quote-btn"
                                className="mt-8 border border-brand-red bg-brand-red px-8 py-4 text-[13px] font-semibold uppercase tracking-wider text-white transition-colors hover:bg-brand-redhover"
                            >
                                Get a Quote
                            </button>
                        </FadeUp>
                        <FadeUp className="relative">
                            <img
                                src={IMAGES.factoryDispatch}
                                alt="Packing and dispatch area at Unimicron Technology India"
                                className="h-full min-h-[300px] w-full object-cover"
                            />
                            <div className="absolute bottom-0 left-0 bg-navy-dark px-5 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-white">
                                Packing & Dispatch Area
                            </div>
                        </FadeUp>
                    </div>
                    <div className="mt-12 grid gap-px border border-slate-200 bg-slate-200 sm:grid-cols-2" data-testid="infra-packaging-points">
                        {[
                            { title: "Protective Packing", desc: "Boards separated and cushioned to prevent surface damage in transit." },
                            { title: "Batch Labelling", desc: "Clear labelling for part number, quantity and batch traceability." },
                            { title: "Moisture Care", desc: "Appropriate packing for surface finish protection during storage." },
                            { title: "Committed Dispatch", desc: "Dispatch planned against the delivery date confirmed with you." },
                        ].map((p, i) => (
                            <FadeUp key={p.title} delay={i * 0.06} className="bg-white p-6">
                                <h3 className="text-[15px] font-semibold text-navy">{p.title}</h3>
                                <p className="mt-2 text-[13px] leading-relaxed text-slate-500">{p.desc}</p>
                            </FadeUp>
                        ))}
                    </div>
                </div>
            </section>
        </>
    );
}
